import { useUserStore } from '~/app/_store/user-store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { clsx } from 'clsx'
import { Comment } from '@basehub/data/replicache/index'
import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { VariantProps, cva } from 'class-variance-authority'
import { FaceIcon } from '@radix-ui/react-icons'
import { emojis } from '../emoji-picker/data'
import { EmojiPicker } from '../emoji-picker'
import { useToggleState } from '~/hooks/use-toggle-state'
import { useUserData } from '~/hooks-data/use-user-data'
import { TooltipContent } from '~/sotano-kit/primitives/tooltip'

export const $reaction = cva(
  'flex py-0.5 px-2 gap-1 group/reaction items-center text-sm rounded-md border cursor-default select-none h-6',
  {
    variants: {
      intent: {
        primary: 'border-dark-accent bg-dark-control-muted text-dark-dim',
        secondary:
          'border-dark-border-solid light:bg-dark-shade light:hover:bg-dark-shade-hover bg-dark-shade-hover text-dark-muted [&>p]:font-medium hover:bg-dark-elevated',
      },
    },
  }
)

type ReactionButtonProps = {
  reaction: React.ReactNode
  number?: number
} & VariantProps<typeof $reaction> &
  React.ComponentProps<'button'>

export const ReactionButton = React.forwardRef(
  (
    { reaction, number, intent, className, ...props }: ReactionButtonProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    return (
      <button {...props} className={$reaction({ intent, className })} ref={ref}>
        <span className="text-[12px] text-dark-muted group-hover/reaction:text-dark-dim [font-family:initial]">
          {reaction}
        </span>
        {number && (
          <p
            style={{
              minWidth: `${number.toString().length}ch`,
            }}
            className="text-dark-dim text-xs font-medium"
          >
            {number}
          </p>
        )}
      </button>
    )
  }
)

export const CommentReactions = ({
  comment,
  isEditingComment,
}: {
  comment: Comment
  isEditingComment?: boolean
}) => {
  const entries = Object.entries(comment.reactions)

  if (!entries.length) return null
  return (
    <div
      className={clsx(
        'flex gap-1 mt-1.5 text-dark-faint flex-wrap',
        isEditingComment && 'hidden'
      )}
    >
      {entries.map(([reaction, usersReactions]) => (
        <Reaction
          key={reaction}
          reaction={reaction}
          usersReactions={usersReactions}
          commentId={comment.id}
        />
      ))}
      <NewCommentReactionButton commentId={comment.id} />
    </div>
  )
}

export const NewCommentReactionButton = ({
  commentId,
}: {
  commentId: string
}) => {
  const state = useToggleState()
  const repoReplicache = useRepoReplicache().data?.replicache
  const user = useUserStore((s) => s.optimisticUser)
  return (
    <TooltipPrimitive.Tooltip>
      <EmojiPicker
        onSelect={(emoji, e) => {
          e.stopPropagation()
          if (!user) return
          repoReplicache?.mutate.addReaction({
            id: commentId,
            reaction: emoji.emoji,
            userId: user.id,
          })
        }}
        state={state}
        data-inner-popover
      >
        <TooltipPrimitive.TooltipTrigger asChild>
          <ReactionButton
            reaction={<FaceIcon className="size-3.5" />}
            intent="secondary"
            className="border-dark-border-solid bg-dark-shade-hover text-dark-muted"
          />
        </TooltipPrimitive.TooltipTrigger>
      </EmojiPicker>
      <TooltipPrimitive.TooltipPortal>
        <TooltipContent
          asChild
          sideOffset={8}
          collisionBoundary={document.body}
        >
          <span>Add reaction...</span>
        </TooltipContent>
      </TooltipPrimitive.TooltipPortal>
    </TooltipPrimitive.Tooltip>
  )
}

export const Reaction = ({
  reaction,
  usersReactions,
  commentId,
}: {
  reaction: string
  usersReactions: string[]
  commentId: string
}) => {
  const currentUser = useUserStore((s) => s.optimisticUser)
  const replicache = useRepoReplicache().data?.replicache
  const emoji = emojis.find((e) => e.emoji === reaction)

  return (
    <TooltipPrimitive.Tooltip>
      <TooltipPrimitive.TooltipTrigger asChild>
        <ReactionButton
          number={usersReactions.length}
          reaction={reaction}
          intent={
            usersReactions.includes(currentUser?.id ?? '')
              ? 'primary'
              : 'secondary'
          }
          onClick={async (e) => {
            e.stopPropagation()
            if (!currentUser) return
            if (usersReactions.includes(currentUser.id)) {
              await replicache?.mutate.removeReaction({
                id: commentId,
                reaction,
                userId: currentUser.id,
              })
              return
            }
            await replicache?.mutate.addReaction({
              id: commentId,
              reaction,
              userId: currentUser.id,
            })
          }}
          className={clsx(
            'flex py-0.5 px-2 gap-1 items-center text-sm rounded border cursor-default select-none',
            usersReactions?.includes(currentUser?.id ?? '')
              ? 'border-dark-accent bg-dark-control-muted text-dark-dim'
              : 'border-dark-border-solid bg-dark-shade-hover text-dark-muted'
          )}
        />
      </TooltipPrimitive.TooltipTrigger>
      <TooltipPrimitive.TooltipPortal>
        <TooltipPrimitive.TooltipContent
          className="!z-tooltip"
          asChild
          sideOffset={8}
          collisionBoundary={document.body}
        >
          <div
            className={clsx(
              'transition-opacity items-center origin-radix-tooltip animate-scale-in-fade duration-75 pl-2 pr-3.5 py-1 light:bg-dark-base light:border-dark-border-solid light:border bg-dark-border-solid rounded-lg max-w-[350px] text-center gap-1 light:[box-shadow:var(--var-current-shadow-light)]',
              !usersReactions.length && 'opacity-0',
              usersReactions.length < 4 && 'flex'
            )}
            style={{
              ['--var-current-shadow-light' as string]:
                '-27px 35px 12px 0px rgba(0, 0, 0, 0.00), -17px 23px 11px 0px rgba(0, 0, 0, 0.00), -10px 13px 10px 0px rgba(0, 0, 0, 0.01), -4px 6px 7px 0px rgba(0, 0, 0, 0.02), -1px 1px 4px 0px rgba(0, 0, 0, 0.02)',
            }}
          >
            <span className="text-[28px] text-center leading-normal font-medium icon [font-family:initial]">
              {reaction}
            </span>
            <p className="text-dark-dim text-xs text-center">
              {emoji?.emojiAlt ?? ''} reacted by{' '}
              <EnumerationOfUsers users={usersReactions} />
            </p>
          </div>
        </TooltipPrimitive.TooltipContent>
      </TooltipPrimitive.TooltipPortal>
    </TooltipPrimitive.Tooltip>
  )
}

const EnumerationOfUsers = ({
  users,
  className,
}: {
  users: string[]
  className?: string
}) => {
  return (
    <>
      {users.map((userId, index) => {
        return (
          <React.Fragment key={index}>
            {index === 0 ? '' : index === users.length - 1 ? ' and ' : ', '}
            <span className={clsx('text-dark-bright font-medium', className)}>
              <UserName userId={userId} />
            </span>
          </React.Fragment>
        )
      })}
    </>
  )
}

const UserName = ({ userId }: { userId: string }) => {
  const { data: user } = useUserData(userId)
  if (!user) return userId
  if (user.isMe) return 'You'
  return user.username
}
